// Scene 5 - Review + Ausfuehrung + Release.
// Rendert Manifest, ChevFlow-Zusammenfassung, PD-Toggle, Start-Button und
// live-Ausfuehrung. Release-Bereich ist admin-only.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "../../../components/Skeleton";
import { useLang } from "../i18n";
import { useTransportV2, hasWerke, buckets, activeWerke, vorwaertsStufen, pdPidsFuerStufe, deploymentSelection, referenceAutoDeploy, refNutzer } from "../context/TransportV2Context";
import { stageLabel } from "../../../utils/stages";
import { useCicdRun } from "../hooks/useCicdRun";
import { useReleaseCandidate } from "../hooks/useReleaseCandidate";
import { usePipelineReady } from "../hooks/usePipelineReady";
import { api } from "../../../utils/helpers";
import { buildConfigsByWerk } from "../../../utils/transport-config-helpers";
import { Cx } from "../components/IconSprite";
import RunLiveCard from "../components/RunLiveCard";

// Wie lange ist der letzte Push her? Der Kandidat liefert einen Zeitstempel, die Zeile zeigte
// bisher pauschal "heute".
function vorWieLange(iso) {
  if (!iso) return "";
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "gerade eben";
  if (min < 60) return `vor ${min} min`;
  const std = Math.floor(min / 60);
  if (std < 24) return `vor ${std} h`;
  const tage = Math.floor(std / 24);
  return tage === 1 ? "vor 1 Tag" : `vor ${tage} Tagen`;
}

function currentUserRole() {
  try {
    const raw = localStorage.getItem("cpi_user");
    if (raw) return JSON.parse(raw)?.role || null;
  } catch {}
  return null;
}

function selectedIflows(state) {
  const cmp = state.precheckByBucket || {};
  const seen = new Set();
  const list = [];
  for (const entry of Object.values(cmp)) {
    if (!entry || !Array.isArray(entry.comparison)) continue;
    for (const a of entry.comparison) {
      if (a.draft) continue;
      const src = a.originalId || a.Id;
      if (seen.has(src)) continue;
      // hat mind. ein Werk in sel?
      const sel = state.sel?.[src];
      if (!sel) continue;
      const anyOn = Object.keys(sel).some((k) => sel[k]);
      if (!anyOn) continue;
      seen.add(src);
      list.push({ id: src, name: a.Name || src, version: a.Version || "-" });
    }
  }
  return list;
}

function collectSelectedArtifactIds(state) {
  return selectedIflows(state).map((f) => f.id);
}

// The selected plants for the snapshot: it creates one package folder per kennung in Git.
// Without plants the list stays empty and only the source package is created.
//
// Jedes Werk traegt seine EIGENE Artefakt-Auswahl mit. Ohne sie kennt das Backend nur die
// flache Liste und vervielfacht sie auf alle Werke - die Abwahl einzelner Zeilen in Schritt 3
// war damit wirkungslos. Quelle ist state.sel[sourceId][werkKey]; der werklose Anteil ("std")
// kommt hier nicht vor, weil bei aktiven Werken kein "std"-Bucket existiert.
// Ein Werk ohne angehaktes Artefakt faellt komplett heraus: ein leeres Zielpaket wuerde im
// SAP-Modus das Paket im Ziel loeschen und leer neu anlegen.
export function collectWerke(state, iflows) {
  if (!hasWerke(state)) return [];
  const sel = state.sel || {};
  const quellIds = (iflows || []).map((f) => f.id);
  return activeWerke(state)
    .filter((w) => w.kennung)
    .map((w) => ({
      kennung: w.kennung,
      name: w.name || w.kennung,
      // Der Werk-Schluessel ist die Kennung (Scene3 schreibt sel mit entry.werk.kennung,
      // die Buckets lesen mit w.key) - beide Schreibweisen pruefen, damit ein abweichender
      // Key die Auswahl nicht still leer laufen laesst.
      artifactIds: quellIds.filter((src) => !!(sel[src]?.[w.key] || sel[src]?.[w.kennung])),
    }))
    .filter((w) => w.artifactIds.length > 0);
}

// Ausgewaehlte globale Referenzen fuer den Snapshot (Vertrag: [{ id, type, typ }]). Sie reisen
// im Snapshot mit und werden vom Ziel-Workflow VOR den iFlows hochgeladen und deployt.
//   - Id VOR Name als Schluessel: der Snapshot laedt per Id (Name != Id liefe in einen 404).
//   - type ist das Label (package-references schreibt es GROSS: Type), typ der Schluessel
//     (ScriptCollection | MessageMapping | ValueMapping). Das Backend bevorzugt typ und faellt
//     auf type zurueck - typ geht deshalb nur mit, wenn es da ist.
//   - Eine Referenz, die nicht mehr in globalRefs steht (Liste zwischenzeitlich neu geladen),
//     bleibt draussen: mit leerem Typ fragte der Snapshot den falschen Entity-Set ab.
//   - transportierbar=false (Function Library u. a.) bleibt IMMER draussen, auch wenn refSel
//     die Referenz irgendwie fuehrt: gemeldet ja, transportiert nie.
export function collectGlobalRefs(state) {
  const out = [];
  for (const [k, v] of Object.entries(state.refSel || {})) {
    if (!v) continue;
    const ref = (state.globalRefs || []).find((r) => (r?.bundleId || r?.id || r?.Id || r?.name || r?.Name) === k);
    if (!ref || ref.transportierbar === false) continue;
    const typ = ref.typ || ref.Typ || "";
    out.push(typ ? { id: k, type: ref.type || ref.Type || "", typ } : { id: k, type: ref.type || ref.Type || "" });
  }
  return out;
}

export default function Scene5Review({ user }) {
  const { t } = useLang();
  const { state, dispatch } = useTransportV2();
  const cicd = useCicdRun();
  const role = user?.role || currentUserRole();
  const isAdmin = role === "admin";
  const rel = useReleaseCandidate(state.spaceId, isAdmin);
  const [pdCounts, setPdCounts] = useState({ pids: 0, chained: 0 });
  const [phases, setPhases] = useState([]);
  const [runReason, setRunReason] = useState("");
  const startedRef = useRef(false);
  const [pendingApproval, setPendingApproval] = useState(false);

  const pipe = usePipelineReady(state.spaceId);
  // SAP mode: transport runs through SAP's own workflows. No Partner Directory there.
  const sapModus = pipe.modus === "sap";

  const iflows = useMemo(() => selectedIflows(state), [state]);
  // Dieselbe Liste speist Anzeige und Snapshot-Payload - beide zeigen also dasselbe.
  const gewaehlteRefs = useMemo(() => collectGlobalRefs(state), [state]);
  const bucketList = buckets(state);
  const deploySelection = deploymentSelection(state);
  const deployDecisions = Object.values(deploySelection.artifacts).flatMap(plants => Object.values(plants));
  const deployCount = deployDecisions.filter(Boolean).length;
  const deploymentModeConflict = sapModus && deployDecisions.some(value => !value);
  const anyDeployment = deployCount > 0 || (state.globalRefs || []).some(ref =>
    gewaehlteRefs.some(selected => selected.id === (ref.bundleId || ref.id || ref.Id || ref.name || ref.Name)) && referenceAutoDeploy(state, ref));

  // Automatisch erkannte Pids (aus BPMN + ProcessDirect-Reverse-Match)
  const [pdAuto, setPdAuto] = useState({ loading: false, primary: [], related: [], error: null });
  // Sammel-Checkbox "Partner Directories replizieren": an heisst "mindestens eine Pid gewaehlt",
  // die erkannten Pids sind die Vorbelegung, auf die "an" zurueckfuehrt.
  const erkanntePids = useMemo(
    () => [...new Set([...(pdAuto.primary || []), ...(pdAuto.related || [])].map((p) => p.pid).filter(Boolean))],
    [pdAuto]
  );
  const pdGewaehlt = useMemo(() => new Set(state.pdSelectedPids || []), [state.pdSelectedPids]);
  // Ohne erkannte Pids spiegelt die Checkbox das Flag direkt - sonst wirkte ein Klick nicht.
  const pdAn = !!state.pdReplicate && (pdGewaehlt.size > 0 || erkanntePids.length === 0);
  // Vollstaendige Kandidaten-Liste vom Quell-Tenant (nur PIDs mit Metadaten)
  const [pdSource, setPdSource] = useState({ loading: false, entries: [], error: null });
  const [pdSearch, setPdSearch] = useState("");
  const iflowsKey = useMemo(() => iflows.map((f) => f.id).sort().join("|"), [iflows]);
  // Merker fuer die Vorbelegung: pro Paket+iFlow-Signatur genau EINMAL. Sonst
  // wuerde ein bewusstes Abwaehlen bei jedem Re-Render zurueckgesetzt.
  const prefillKeyRef = useRef(null);

  // Erkannte Pids + Zaehlung laden (aus einer Antwort).
  // Antwortform (siehe backend/server.js /api/transport/partner-references):
  //   partners:        primary + related (aufgeloeste Kette)
  //   primaryPartners: direkt referenzierte Pids - entweder als String (klassisch)
  //                    ODER als Objekt { pid, via: "reference"|"header", sender, iface, msgType }
  //                    (Backend-Erweiterung: Header-Erkennung SAP_Sender/SAP_SenderInterface).
  //   relatedPartners: verkettete Pids (aus Determination-Aufloesung)
  //   partnerRoles:    { pid: "primary"|"related" }
  useEffect(() => {
    if (sapModus) return;
    if (!state.sourceInstanceId || !state.pkgId) return;
    if (!iflows.length) {
      setPdAuto({ loading: false, primary: [], related: [], error: null });
      setPdCounts({ pids: 0, chained: 0 });
      return;
    }
    let cancelled = false;
    setPdAuto((s) => ({ ...s, loading: true, error: null }));
    api("/api/transport/partner-references", {
      method: "POST",
      body: JSON.stringify({
        sourceInstanceId: state.sourceInstanceId,
        targetInstanceId: state.space?.qasInstance?.id,
        sourcePackageId: state.pkgId,
        artifactIds: iflows.map((f) => f.id),
      }),
    })
      .then((r) => {
        if (cancelled) return;
        // Beide Formen tolerant lesen: Objekt-Eintraege behalten via/sender/iface,
        // Strings werden auf {pid, via:"reference"} normalisiert. Bei Duplikaten
        // (selber Pid, einmal via reference, einmal via header) gewinnt der erste.
        const norm = (arr) => {
          const out = []; const gesehen = new Set();
          for (const raw of Array.isArray(arr) ? arr : []) {
            let pid, via = "reference", sender = null, iface = null, msgType = null;
            if (typeof raw === "string") { pid = raw; }
            else if (raw && typeof raw === "object") {
              pid = raw.pid || raw.Pid || raw.id;
              via = raw.via || "reference";
              sender = raw.sender || null;
              iface = raw.iface || raw.interface || null;
              msgType = raw.msgType || raw.messageType || null;
            }
            if (!pid || gesehen.has(pid)) continue;
            gesehen.add(pid);
            out.push({ pid, via, sender, iface, msgType });
          }
          return out;
        };
        // Das Backend liefert via/Header als Top-Level-Maps (pid-basiert), nicht am Eintrag:
        //   partnerVia:     { [pid]: ["header"|"address"|"reference"] }
        //   partnerHeaders: { [pid]: { sender, iface, msgType } }
        // Hier auf die pro-Eintrag-Form ziehen, die das UI erwartet (Header-Kontext-Hinweis).
        // Sind die Eintraege schon Objekte mit via/sender (kuenftige Backend-Form), bleibt das erhalten.
        const viaMap = r?.partnerVia || {};
        const hdrMap = r?.partnerHeaders || {};
        const anreichern = (p) => {
          const w = viaMap[p.pid];
          const h = hdrMap[p.pid];
          const via = Array.isArray(w) ? (w.includes("header") ? "header" : (w[0] || p.via)) : p.via;
          return { ...p, via, sender: h?.sender ?? p.sender, iface: h?.iface ?? p.iface, msgType: h?.msgType ?? p.msgType };
        };
        const primary = norm(r?.primaryPartners).map(anreichern);
        const related = norm(r?.relatedPartners).map(anreichern);
        setPdAuto({ loading: false, primary, related, error: null });
        setPdCounts({ pids: primary.length, chained: related.length });
        // Vorbelegung: genau EINMAL pro (Paket + iFlow-Signatur). Ein bewusstes
        // Abwaehlen soll erhalten bleiben - der Merker verhindert das erneute
        // Ueberschreiben, weil der Effekt bei jeder Response neu laeuft.
        const detectedPids = [...new Set([...primary, ...related].map((p) => p.pid))];
        const key = `${state.pkgId}::${iflowsKey}`;
        if (prefillKeyRef.current !== key) {
          prefillKeyRef.current = key;
          dispatch({ type: "SET_PD_SELECTED_PIDS", payload: detectedPids });
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setPdAuto({ loading: false, primary: [], related: [], error: e?.message || "Ermittlung fehlgeschlagen" });
        setPdCounts({ pids: 0, chained: 0 });
      });
    return () => { cancelled = true; };
    // Wir haengen bewusst NICHT an state.pdSelectedPids - sonst wuerde jedes
    // Abwaehlen den Effekt neu triggern und die Vorbelegungslogik verwirren.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.sourceInstanceId, state.pkgId, state.space, iflowsKey, dispatch, sapModus]);

  // Volle PD-Liste vom Quell-Tenant laden (fuer manuelles Hinzufuegen).
  // Antwortform: { partners: [{ Pid, stringParams:[{Id,Value}], binaryParams:[{Id,ContentType}], altPartners:[...], systemGrund }] }
  // systemGrund != null -> systemeigene Kennung, nicht auswaehlbar.
  useEffect(() => {
    if (sapModus) return; // SAPs Weg traegt kein Partner Directory - Quell-Tenant nicht befragen
    if (!state.sourceInstanceId) return;
    // Kein Guard auf pdReplicate mehr: die Liste ist immer sichtbar, also muss sie auch geladen
    // werden - sonst bliebe "Eigene Eintraege" leer, sobald die Vorbelegung 0 Pids ergab.
    let cancelled = false;
    setPdSource((s) => ({ ...s, loading: true, error: null }));
    api(`/api/${state.sourceInstanceId}/partner-directory`)
      .then((r) => {
        if (cancelled) return;
        const entries = Array.isArray(r?.partners) ? r.partners : [];
        setPdSource({ loading: false, entries, error: null });
      })
      .catch((e) => {
        if (cancelled) return;
        setPdSource({ loading: false, entries: [], error: e?.message || "Laden fehlgeschlagen" });
      });
    return () => { cancelled = true; };
  }, [state.sourceInstanceId, sapModus]);

  const startTransport = useCallback(async () => {
    if (startedRef.current || deploymentModeConflict || pipe.loading || !pipe.ready) return;
    startedRef.current = true;
    // Im SAP-Modus faellt das Partner Directory hier aus - der Snapshot legte sonst Dateien nach
    // Git, die SAPs Workflow nicht liest.
    const usePd = !sapModus && !!state.pdReplicate;
    // Zaehlt effektiv nur, wenn Haken UND mindestens eine Kennung ausgewaehlt ist.
    // Ohne Auswahl waere die PD-Phase eine leere Anzeige.
    const selectedPidsNow = usePd ? Array.from(new Set(state.pdSelectedPids || [])) : [];
    const effectivelyPd = usePd && selectedPidsNow.length > 0;
    // Keine eigene PD-Phase mehr: das Partner Directory reist im Snapshot (git) mit und wird vom
    // Ziel-Workflow (wf) uebernommen. Es gibt keinen separaten Direktschreib-Schritt.
    const phaseList = [
      { id: "git", title: t("ph.git"), icon: "ic-git", logs: [] },
      { id: "wf", title: t("ph.wf"), icon: "ic-play", logs: [] },
    ];
    setPhases(phaseList.map((p) => ({ ...p, status: "waiting" })));

    const setPhase = (id, patch) => setPhases((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    const appendLog = (id, line) => setPhases((prev) => prev.map((p) => (p.id === id ? { ...p, logs: [...p.logs, line] } : p)));

    // Phase git
    setPhase("git", { status: "running" });
    for (const f of iflows) appendLog("git", t("log.zip", { name: f.name }));
    appendLog("git", t("log.extract", { pkg: state.pkgName || state.pkgId }));
    if (hasWerke(state)) {
      for (const f of iflows) appendLog("git", t("log.cfg", { id: f.id, n: activeWerke(state).length }));
    }
    // Partner Directory reist im Snapshot mit (kein Direktschreiben mehr) - der Ziel-Workflow
    // uebernimmt die ausgewaehlten Eintraege per Merge-Upsert.
    if (effectivelyPd) appendLog("git", t("log.pdSnap", { n: selectedPidsNow.length }));

    setPhase("wf", { status: "waiting" });

    try {
      // Parameter values per plant and stage. The backend turns them into SAP's parameter file
      // per target package and merges with the Git stock - stages absent here stay unchanged in
      // Git.
      //
      // state.gitCfg has to come along: only against the Git state can "the user removed the
      // deviation" be told apart from "this stage was never maintained". Without the comparison a
      // deleted deviation would stay in Git forever.
      const configsByWerk = buildConfigsByWerk(state, vorwaertsStufen(state), state.gitCfg);
      const werke = collectWerke(state, iflows);
      // Landet als Git-Commit-Message im Kundenrepo, deshalb englisch und ohne Strich-Einschub.
      const commitMessage = `Transport ${state.pkgName || state.pkgId} (${iflows.length} artifacts)`;

      // Final ausgewaehlte Pids (erkannt-ausgewaehlt + manuell hinzugefuegt, dedupliziert).
      // Bei pdReplicate=false bleibt die Liste leer -> kein Snapshot-Export der PD-Dateien.
      const selectedPids = selectedPidsNow;
      // Ziel-Stufe des aktuellen Dispatchs (normalisiert wie Backend-normStage: lowercase,
      // ohne Bindestrich). Wird sowohl zum Filtern der Dispatch-Pids als auch fuer den
      // Snapshot-Kontrakt (pdStagesByPid) verwendet.
      const targetStageKey = String(state.space?.zielStufe || state.space?.qasInstance?.stage || "qas")
        .toLowerCase()
        .replaceAll("-", "");
      // Etappe C: Nur Pids, deren Stufen-Filter die AKTUELLE Zielstufe (oder implizit "alle Stufen")
      // enthaelt, laufen als partnerIds in den Dispatch. Pids, die nur fuer spaetere Stufen
      // (z. B. nur PROD) markiert sind, liegen zwar im Snapshot inkl. .stages.json, aber der
      // Ziel-Workflow ueberspringt sie hier - sie greifen erst beim spaeteren PROD-Release.
      const dispatchPids = usePd
        ? pdPidsFuerStufe({ pdSelectedPids: selectedPids, pdStagesByPid: state.pdStagesByPid }, targetStageKey)
        : [];
      // pdStagesByPid geht IMMER mit in den Snapshot (Backend erwartet es schon). Fehlt der
      // Filter fuer eine Pid, schreibt der Export "*" (Altverhalten).
      const pdStagesByPidPayload = usePd ? (state.pdStagesByPid || {}) : {};

      const res = await cicd.start({
        spaceId: state.spaceId,
        sourceInstanceId: state.sourceInstanceId,
        packageId: state.pkgId,
        packageName: state.pkgName || state.pkgId,
        artifactIds: iflows.map((f) => f.id),
        // Ausgewaehlte globale Referenzen (siehe collectGlobalRefs): Typ-Label und Typ-Schluessel
        // gehen mit, nicht transportierbare Referenzen bleiben draussen.
        globalRefs: sapModus ? gewaehlteRefs : gewaehlteRefs.map(r => {
          const ref = (state.globalRefs || []).find(x => (x.bundleId || x.id || x.Id || x.name || x.Name) === r.id);
          return { ...r, deploy: referenceAutoDeploy(state, ref), referencedBy: refNutzer(ref) };
        }),
        deployment: sapModus ? undefined : deploymentSelection(state),
        werke,
        configsByWerk,
        // Snapshot bekommt ALLE ausgewaehlten Pids (auch die nur fuer spaetere Stufen) - damit
        // ihre PD-Dateien inkl. .stages.json in Git liegen, bereit fuer den PROD-Release.
        snapshotPartnerIds: selectedPids,
        // Dispatch bekommt nur die Pids, die JETZT (QAS-Stufe) landen sollen.
        partnerIds: dispatchPids,
        pdStagesByPid: pdStagesByPidPayload,
        commitMessage,
        // Zielstufe aus der Schiene des Space, nicht fest "qas": bei DEV + PRE-PROD + PROD ist
        // das Ziel PRE-PROD. Der alte Fallback haette den Dispatch nach QAS geschickt, wo es
        // gar kein Environment gibt.
        targetStage: targetStageKey,
      });

      appendLog("git", t("log.commit"));
      setPhase("git", { status: "done" });

      setPhase("wf", { status: "running" });
      appendLog("wf", t("log.wf", {
        // Der Name muss dem Modus folgen - sonst behauptet das Protokoll einen Workflow, der
        // gar nicht gelaufen ist.
        wf: sapModus ? "btp-sap-transport.yml" : "btp-artifact-transport.yml",
        s: String(state.space?.zielStufe || state.space?.qasInstance?.stage || "qas").toLowerCase(),
      }));
      if (res.pendingApproval) {
        appendLog("wf", t("log.pending"));
        setPhase("wf", { status: "done" });
        setPendingApproval(true);
      } else if (res.runId) {
        appendLog("wf", t("log.queued", { n: res.runId }));
        setPhase("wf", { status: "done" });
      } else {
        // Kein Lauf auffindbar: der Dispatch ging raus, GitHub meldet aber keine Lauf-Id.
        // Der Startknopf muss wieder bedienbar werden - sonst haengt der Assistent in einem
        // Zustand fest, aus dem nur ein Neuladen der Seite herausfuehrt.
        appendLog("wf", t("run.noRunFound"));
        setPhase("wf", { status: "done" });
        startedRef.current = false;
      }
    } catch (e) {
      const failedId = phases.find((p) => p.status === "running")?.id || "wf";
      setPhase(failedId, { status: "error", error: e?.message || "Fehler" });
      startedRef.current = false;
    }
  }, [state, iflows, gewaehlteRefs, cicd, t, phases, sapModus, deploymentModeConflict, pipe.loading, pipe.ready]);

  const [releaseReason, setReleaseReason] = useState("");
  const [relError, setRelError] = useState(null);
  const relReasonOk = releaseReason.trim().length >= 3;
  // /api/cicd/release-candidate antwortet {configured, tokenSet, project, candidate} - der
  // Kandidat liegt EINE Ebene tiefer. Vorher wurde rel.data.summary/.items gelesen, das ist
  // immer undefined: die Kacheln zeigten die Zahlen des laufenden Assistenten statt der seit
  // dem letzten Release aufgelaufenen, und die Liste zeigte die iFlows dieses Transports.
  const relCand = rel.data?.candidate || null;
  // One candidate per package. Every plant is a package of its own with its own state, and
  // exactly one of them gets released. The UI used to block as soon as several packages had piled
  // up, because a release can only dispatch one package and nobody could tell which one was
  // meant. Instead of blocking, it now asks.
  const relKandidaten = useMemo(() => (Array.isArray(relCand?.kandidaten) ? relCand.kandidaten : []), [relCand]);
  const [relPkgId, setRelPkgId] = useState(null);
  const relAktiv = relKandidaten.find((k) => k.packageId === relPkgId) || relKandidaten[0] || null;
  // Die Nutzlast des Release kommt AUS dem Kandidaten. Ohne sie verlangt
  // POST /api/cicd/transport/release packageId und artifactIds und lehnt jeden Klick mit 400 ab.
  const relPayload = relAktiv?.releasePayload || null;
  // Nach dem Transport nach QAS steht ein neuer Lauf im Log. Ohne Nachladen zeigt der
  // Freigabe-Bereich den Stand von VOR dem Transport und hat keine Nutzlast fuer den Release.
  const relReload = rel.reload;
  const relNachgeladen = useRef(false);
  useEffect(() => {
    if (cicd.state === "done" && !relNachgeladen.current) {
      relNachgeladen.current = true;
      relReload();
    }
  }, [cicd.state, relReload]);

  const releaseNow = useCallback(async () => {
    setRelError(null);
    if (!relPayload) { setRelError(t("rel.noPkg")); return; }
    try {
      await rel.releaseNow({ approvalNote: releaseReason.trim(), extra: relPayload });
      setReleaseReason("");
    } catch (e) {
      setRelError(e?.message || "Release fehlgeschlagen");
    }
  }, [rel, releaseReason, relPayload, t]);

  const canStart = iflows.length > 0 && !!state.sourceInstanceId && !!state.pkgId;
  const running = cicd.state === "snapshotting" || cicd.state === "dispatching" || cicd.state === "running";
  // PROD-Riegel: der Dispatch liegt in der Transport-Freigabe und ein zweiter Klick prallt
  // wirkungslos ab. Der Knopf gehoert deshalb gesperrt und beschriftet - vorher sah er aktiv
  // aus und tat nichts.
  const wartetAufFreigabe = cicd.state === "pending";
  // Im SAP-Modus laeuft der Transport ueber SAPs Workflows: paketweise, mit Loeschen und ohne
  // Partner Directory. Die Werke bleiben - ihr Split passiert im Snapshot, nicht im Workflow.
  const startBtnDisabled = !canStart || running || wartetAufFreigabe || cicd.state === "done" || pipe.loading || !pipe.ready || deploymentModeConflict;

  const s = state.space;
  const stages = s?.stages || [];
  const tgtIdx = Math.max(0, stages.length - 2);

  const runJobs = useMemo(() => {
    if (cicd.runInfo?.jobs?.length) {
      const flat = [];
      for (const j of cicd.runInfo.jobs) {
        for (const st of j.steps || []) {
          flat.push({ name: st.name, status: st.status, duration: st.completed_at && st.started_at ? Math.max(1, Math.round((new Date(st.completed_at) - new Date(st.started_at)) / 1000)) + "s" : "" });
        }
      }
      return flat.slice(0, 12);
    }
    // Fallback: aus dem Mock generieren
    const steps = [t("job.checkout")];
    for (let i = 1; i < stages.length - 1; i++) steps.push(t("job.prep", { code: stages[i].code }));
    for (const f of iflows) steps.push(t("job.upload", { name: f.name }));
    if (sapModus || anyDeployment) {
      steps.push(t("job.deploy"));
      steps.push(t("job.smoke", { code: stages[tgtIdx]?.code || "QAS" }));
    }
    return steps.map((name) => ({ name, status: "waiting" }));
  }, [cicd.runInfo, stages, iflows, tgtIdx, t, sapModus, anyDeployment]);

  const runNumber = cicd.runInfo?.runNumber || cicd.runId || null;
  const runStatus = cicd.state === "done" ? "success" : cicd.state === "failed" ? "failed" : "running";
  const runMeta = cicd.runInfo?.actor ? `${cicd.runInfo.actor} · ${cicd.runInfo.event || "workflow_dispatch"}` : "";
  const done = cicd.state === "done";

  useEffect(() => {
    if (done) dispatch({ type: "SET_PIPELINE_STATE", payload: { pipeline: "done" } });
  }, [done, dispatch]);

  return (
    <section className="scene active scene-enter" id="s5">
      <div className="sh">
        <h2>{t("s5.h2")}</h2>
        <p>{t("s5.p")}</p>
      </div>

      <div className="manifest">
        <div className="mf-cell">
          <span className="mf-k">{t("mf.space")}</span>
          <span className="mf-v">{s?.name || "-"}</span>
          <span className="mf-s">
            {stages.map((st, i) => (
              <React.Fragment key={"m" + st.code + i}>
                {i > 0 && <Cx />}<span>{st.code}</span>
              </React.Fragment>
            ))}
          </span>
        </div>
        <div className="mf-cell">
          <span className="mf-k">{t("mf.pkg")}</span>
          <span className="mf-v">{state.pkgName || "-"}</span>
          <span className="mf-s">{state.pkgId || ""}</span>
        </div>
        <div className="mf-cell">
          <span className="mf-k">{t("mf.iflows")}</span>
          <span className="mf-v">{t("mf.sel", { n: iflows.length })}</span>
          <span className="mf-s">
            {hasWerke(state)
              ? t("mf.rowsW", { n: Object.values(state.sel || {}).reduce((acc, s2) => acc + Object.values(s2 || {}).filter(Boolean).length, 0), m: activeWerke(state).length })
              : t("mf.rows", { n: Object.values(state.sel || {}).reduce((acc, s2) => acc + Object.values(s2 || {}).filter(Boolean).length, 0) })}
          </span>
          {!sapModus && <span className="mf-s">{t("deploy.summary", { on: deployCount, off: deployDecisions.length - deployCount })}</span>}
        </div>
        <div className="mf-cell">
          <span className="mf-k">{t("mf.refs")}</span>
          <span className="mf-v">{gewaehlteRefs.length}</span>
          <span className="mf-s">{gewaehlteRefs.map((r) => (r.type ? `${r.id} (${r.type})` : r.id)).join(", ") || "-"}</span>
        </div>
        <div className="mf-cell">
          <span className="mf-k">{t("mf.cfg")}</span>
          <span className="mf-v">{t("mf.req", { n: 0 })}</span>
          <span className="mf-s">{t("mf.allset")}</span>
        </div>
      </div>

      <div className="chevflow">
        <span>{t("cf.iflows", { n: iflows.length })}</span>
        <Cx />
        <span>{state.pkgId || ""}</span>
        {hasWerke(state) && <><Cx /><span>{t("rt.werke")} <span className="ken">{activeWerke(state).map((w) => w.kennung).join(" ")}</span></span></>}
        <Cx />
        <span>{stages[tgtIdx]?.code || "QAS"} · {stages[tgtIdx]?.inst || ""}</span>
        <Cx />
        <span>PROD (PD) · {stages[stages.length - 1]?.inst || ""}</span>
      </div>

      {!pipe.loading && !pipe.ready && (
        <div className="pipe-block">
          <svg className="ic"><use href="#ic-info" /></svg>
          <div className="pb-tx">
            <b>{t("s5.noPipeTitle")}</b>
            <div className="pb-sub">{pipe.configured && !pipe.tokenSet ? t("s5.noPipeToken") : t("s5.noPipeCfg")}</div>
          </div>
          <a className="btn btn-sec" href="/cicd-setup">{t("s5.noPipeLink")}</a>
        </div>
      )}

      {/* SAP-Modus: SAPs Transport traegt kein Partner Directory mit. Der Schalter wird deshalb
          ausgeblendet, nicht ausgegraut - ein Bedienelement ohne Wirkung ist schlimmer als
          keines. Stattdessen ein Satz, der sagt, was hier anders laeuft. */}
      {(pipe.loading || pipe.ready) && sapModus && (
        <div className="pd-row" style={{ borderLeft: "2px solid var(--warn)", paddingLeft: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label>{t("s5.sapModus")}</label>
            <div className="pd-sub">{t("s5.sapModusSub")}</div>
            {deploymentModeConflict && <div className="pd-sub" role="alert">{t("deploy.sap")}</div>}
          </div>
        </div>
      )}
      {(pipe.loading || pipe.ready) && !sapModus && (<>
      <div className="pd-row">
        <input
          type="checkbox"
          className="cb"
          // Sammel-Checkbox wie die Kopf-Checkboxen der vorherigen Schritte: an = alle erkannten
          // Partner-IDs, aus = keine (der Bereich bleibt sichtbar), Teilauswahl = indeterminate.
          checked={pdAn}
          ref={(el) => { if (el) el.indeterminate = pdAn && erkanntePids.some((p) => !pdGewaehlt.has(p)); }}
          onChange={(e) => {
            if (e.target.checked) {
              dispatch({ type: "SET_PD_REPLICATE", payload: true });
              if (erkanntePids.length) dispatch({ type: "SET_PD_SELECTED_PIDS", payload: erkanntePids });
            } else {
              dispatch({ type: "SET_PD_REPLICATE", payload: false });
            }
          }}
          style={{ marginTop: 3 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <label>{t("s5.pd")}</label>
          <span className="pd-info" tabIndex={0} aria-label={t("s5.pdInfoTitle")}>
            <svg className="ic"><use href="#ic-info" /></svg>
            <div className="pd-info-pop" role="tooltip">
              <div className="pi-title">{t("s5.pdInfoTitle")}</div>
              <ul>
                <li>{t("s5.pdInfoDirect")}</li>
                <li>{t("s5.pdInfoAddr")}</li>
                <li>{t("s5.pdInfoChain")}</li>
              </ul>
              <div className="pi-miss">{t("s5.pdInfoMiss")}</div>
            </div>
          </span>
          <div className="pd-sub">{pdAn ? t("s5.pdSub", { n: pdCounts.pids || 0, m: pdCounts.chained || 0 }) : t("s5.pdNoneSelected")}</div>
        </div>
      </div>

        <PdPicker
          t={t}
          pdAuto={pdAuto}
          pdSource={pdSource}
          pdSearch={pdSearch}
          setPdSearch={setPdSearch}
          selectedPids={state.pdSelectedPids || []}
          pdStagesByPid={state.pdStagesByPid || {}}
          // Bei einer einzigen Vorwaerts-Stufe waere ein Umschalter sinnlos - PdPicker blendet
          // ihn dann komplett aus und die Ansicht ist wie heute.
          forwardStages={vorwaertsStufen(state)}
          onToggle={(pid) => dispatch({ type: "TOGGLE_PD_PID", payload: pid })}
          onSetAll={(pids) => dispatch({ type: "SET_PD_SELECTED_PIDS", payload: pids })}
          onToggleStage={(pid, stage) => dispatch({ type: "TOGGLE_PD_STAGE", payload: { pid, stage } })}
        />

      </>)}

      {/* Der Startknopf steht AUSSERHALB beider Modus-Bloecke: er gehoert zu keinem von beiden,
          und im SAP-Modus gaebe es sonst nichts mehr zu druecken. */}
      {(pipe.loading || pipe.ready) && (
        <div className="cta-row">
          <button className="btn btn-cta" onClick={startTransport} disabled={startBtnDisabled}>
            {running ? t("s5.running") : wartetAufFreigabe ? t("s5.waitApproval") : (<><svg className="ic"><use href="#ic-play" /></svg>{t("s5.start")}</>)}
          </button>
          <span className="foot-hint">Branch development · {sapModus ? "btp-sap-transport.yml" : "btp-artifact-transport.yml"}</span>
        </div>
      )}

      {(pendingApproval || wartetAufFreigabe) && (
        <div className="sect panel panel-pad">
          <div className="st st--warn"><i className="dot" />{t("s5.pendingApproval")}</div>
          {/* Ohne diesen Satz sieht der Anwender nur "wartet" und sucht den Fehler im
              Assistenten - der naechste Schritt passiert aber in der Transport-Freigabe. */}
          <div className="pd-sub" style={{ marginTop: 6 }}>{t("s5.pendingApprovalSub")}</div>
        </div>
      )}

      {phases.length > 0 && (
        <div className="sect">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 14, flexWrap: "wrap", marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>{t("s5.execTitle")}</h3>
            <span className="tiny muted">{state.pdReplicate && (state.pdSelectedPids || []).length > 0 ? t("s5.metaWith", { user: user?.displayName || user?.username || "system" }) : t("s5.metaWithout", { user: user?.displayName || user?.username || "system" })}</span>
          </div>
          <div>
            {phases.map((p) => (
              <div key={p.id} className={"phase-card " + (p.status === "running" ? "run" : p.status === "done" ? "done" : p.status === "error" ? "run" : "")}>
                <div className="pc-h">
                  <span className="ph-ind">
                    {p.status === "running" && <span className="spinner" />}
                    {p.status === "done" && <svg className="ic ic-16 ph-check"><use href="#ic-check" /></svg>}
                  </span>
                  <svg className="ic" style={{ color: "var(--t3)" }}><use href={"#" + p.icon} /></svg>
                  <span className="pc-title">{p.title}</span>
                  <span className="tiny muted">{p.status === "done" ? t("s5.done") : ""}</span>
                </div>
                <div className="pc-log">
                  {p.logs.map((line, i) => (
                    <div key={i} className="log-line in"><span className="ok-mark">✓</span>{line}</div>
                  ))}
                  {p.error && <div className="errline">{p.error}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Partial failure: with several plants each target package gets its own run. If one of
          them fails to start the rest keeps going - the user must not miss that, otherwise
          they take the transport for complete. The bar carries the colour, the text stays
          readable (a semantic colour does not carry as text colour).
          Der Block steht bewusst AUSSERHALB der Lauf-Karte: nicht ladbare Artefakte, nicht
          uebernommene Partner-Kennungen und Pakete ohne Lauf muessen auch dann sichtbar sein,
          wenn gar kein Lauf zustande kam. */}
      {(cicd.error || (cicd.meldungen || []).length > 0) && (
        <div className="sect">
          {cicd.error && (
            <div
              role="status"
              style={{
                padding: "8px 12px",
                borderLeft: "2px solid var(--warn)",
                background: "color-mix(in srgb, var(--warn) 8%, transparent)",
                fontSize: 13,
                color: "var(--t1)",
              }}
            >
              {cicd.error}
            </div>
          )}
          {(cicd.meldungen || []).length > 0 && (
            <>
              <div className="grp-label" style={{ margin: "14px 0 6px" }}>{t("s5.msgTitle")}</div>
              {cicd.meldungen.map((m, i) => {
                const farbe = m.art === "fehler" ? "var(--crit)" : "var(--warn)";
                return (
                  <div
                    key={i}
                    style={{
                      padding: "6px 12px",
                      marginBottom: 4,
                      borderLeft: `2px solid ${farbe}`,
                      background: `color-mix(in srgb, ${farbe} 7%, transparent)`,
                      fontSize: 12.5,
                      color: "var(--t1)",
                    }}
                  >
                    {m.text}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {(cicd.state === "running" || cicd.state === "done" || cicd.state === "failed") && !pendingApproval && (
        <div className="sect">
          <RunLiveCard
            stages={stages}
            tgtIdx={tgtIdx}
            runInfo={cicd.runInfo}
            jobs={runJobs}
            runNumber={runNumber}
            runStatus={runStatus}
            runMeta={runMeta}
            done={done}
            doneText={t("s5.qasOk")}
            doneSub={t("s5.qasOkSub")}
          />
        </div>
      )}

      {isAdmin && pipe.ready && (cicd.state === "done" || relKandidaten.length > 0) && (
        <div className="sect">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 14, flexWrap: "wrap", marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>{t("rel.title")}</h3>
            <span className="tiny muted">{t("rel.meta")}</span>
          </div>
          <div className="rel-tiles">
            <div className="rel-tile"><div className="rt-v">{relAktiv?.summary?.iflows ?? iflows.length}</div><div className="rt-k">{t("rt.iflows")}</div></div>
            <div className="rel-tile"><div className="rt-v">{relAktiv?.summary?.pushes ?? 1}</div><div className="rt-k">{t("rt.pushes", { cx: <Cx /> })}</div></div>
            <div className="rel-tile"><div className="rt-v">{relCand?.summary?.pakete ?? 1}</div><div className="rt-k">{t("rt.pakete")}</div></div>
            <div className="rel-tile"><div className="rt-v">{relAktiv?.summary?.iflows ?? iflows.length}</div><div className="rt-k">{t("rt.prod")}</div></div>
          </div>
          {/* Only ask when there is something to decide: with a single package the view stays as
              lean as before. */}
          {relKandidaten.length > 1 && (
            <div className="rel-list" role="radiogroup" aria-label={t("rel.pickPkg")} style={{ marginBottom: 12 }}>
              <div className="rel-row" style={{ background: "color-mix(in srgb, var(--bg2) 55%, transparent)" }}>
                <b className="tiny" style={{ letterSpacing: ".1em", textTransform: "uppercase", color: "var(--t3)" }}>{t("rel.pickPkg")}</b>
              </div>
              {relKandidaten.map((k) => {
                const gewaehlt = k.packageId === (relAktiv?.packageId || "");
                return (
                  <label key={k.packageId} className="rel-row" style={{ cursor: "pointer", alignItems: "center" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <input
                        type="radio"
                        name="rel-paket"
                        checked={gewaehlt}
                        onChange={() => setRelPkgId(k.packageId)}
                        style={{ accentColor: "var(--signal)" }}
                      />
                      <b>{k.packageName || k.packageId}</b>
                      <span className="art-id">· {k.packageId}</span>
                    </span>
                    <span className="rr-meta">{t("rel.pkgMeta", { n: k.summary.iflows, wann: vorWieLange(k.lastAt) })}</span>
                  </label>
                );
              })}
            </div>
          )}
          <div className="rel-list">
            <div className="rel-row" style={{ background: "color-mix(in srgb, var(--bg2) 55%, transparent)" }}>
              <b className="tiny" style={{ letterSpacing: ".1em", textTransform: "uppercase", color: "var(--t3)" }}>{t("rel.accum")}</b>
            </div>
            {(relAktiv?.items?.length ? relAktiv.items : iflows.map((f) => ({ iflow: f.name, packageId: state.pkgId }))).map((it, i) => (
              <div key={i} className="rel-row">
                <span><b>{it.iflow || it.name}</b> <span className="art-id">· {it.packageId || state.pkgId}</span></span>
                <span className="rr-meta">
                  {it.pushes
                    ? t("rel.push", { n: it.pushes, cx: <Cx />, wann: vorWieLange(it.lastAt) })
                    : t("rel.today", { cx: <Cx /> })}
                </span>
              </div>
            ))}
          </div>
          <div className="panel rel-form">
            <label className="grp-label" style={{ margin: 0 }}>{t("rel.reason")}</label>
            <textarea
              value={releaseReason}
              placeholder={t("rel.ph")}
              onChange={(e) => setReleaseReason(e.target.value)}
              disabled={rel.releasing}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 14, alignItems: "center" }}>
              <span className="foot-hint">
                {!relPayload ? t("rel.noPkg") : relReasonOk ? t("rel.ready") : t("rel.min3")}
              </span>
              <button className="btn btn-cta" onClick={releaseNow} disabled={!relReasonOk || !relPayload || rel.releasing}>
                {rel.releasing ? t("rel.running") : t("rel.btn")}
              </button>
            </div>
            {(relError || rel.releaseError) && (
              <div className="errline">{relError || rel.releaseError}</div>
            )}
          </div>
        </div>
      )}

      <div className={"final-banner" + (state.finalized ? " show" : "")}>
        <h3>{t("fin.h")}</h3>
        <p>{t("fin.txt", { name: state.pkgName || state.pkgId })}</p>
        <button className="btn btn-sec" onClick={() => dispatch({ type: "RESET_ALL" })}>{t("fin.restart")}</button>
      </div>

      <div className="footbar">
        <button className="btn btn-ghost" onClick={() => dispatch({ type: "SET_STEP", payload: 4 })}>
          <svg className="ic ic-16"><use href="#ic-collapse" /></svg> {t("common.back")}
        </button>
        <span className="foot-hint">{t("s5.footHint")}</span>
      </div>
    </section>
  );
}

// ============================================================================
// PdPicker - zwei Bloecke fuer die Partner-Directory-Auswahl:
//   1) Automatisch erkannt (aus BPMN + ProcessDirect-Reverse-Match) - vorausgewaehlt
//   2) Eigene hinzufuegen (volle Liste vom Quell-Tenant, systemGrund ausgegraut)
// Bewusst als Untervkomponente in derselben Datei - kein neues File fuer eine
// Ansicht, die nur in Scene5Review existiert.
// ============================================================================
function PdPicker({ t, pdAuto, pdSource, pdSearch, setPdSearch, selectedPids, pdStagesByPid, forwardStages, onToggle, onSetAll, onToggleStage }) {
  const selSet = new Set(selectedPids);
  // Normalisiert: primary/related sind Arrays von {pid, via, sender, iface, msgType}.
  const primary = pdAuto.primary || [];
  const related = pdAuto.related || [];
  // Pid-Menge (ohne Doppelte) - fuer den Ausschluss aus "Eigene Eintraege".
  const detectedPidSet = new Set([...primary.map((p) => p.pid), ...related.map((p) => p.pid)]);
  // Anzeigereihenfolge: primary vor related; wenn ein Pid in beiden vorkommt,
  // reicht der primaere Eintrag - der related-Duplikat waere doppelte Zeile.
  const primaryPids = new Set(primary.map((p) => p.pid));
  const anzeige = [
    ...primary.map((p) => ({ ...p, rolle: p.via === "header" ? "header" : "primary" })),
    ...related.filter((p) => !primaryPids.has(p.pid)).map((p) => ({ ...p, rolle: "related" })),
  ];

  // Stufen-Umschalter: nur bei mehreren Vorwaerts-Stufen sinnvoll. Bei genau einer
  // Vorwaerts-Stufe (Ein-Stufen-Space, Ziel-Stufe = Endstufe) verschwindet der Block
  // komplett und die Ansicht ist identisch zum Verhalten vor Etappe C.
  const stufenListe = Array.isArray(forwardStages) ? forwardStages : [];
  const hatMehrereStufen = stufenListe.length > 1;
  const pidStufen = pdStagesByPid || {};
  const allStageKeys = stufenListe.map((s) => s.key);
  // Renderer fuer die Stufen-Zeile eines Pids. Der Aufrufer entscheidet, ob die Zeile
  // ueberhaupt gezeigt wird (nur wenn Pid ausgewaehlt UND mehr als eine Stufe existiert).
  const renderStufen = (pid) => {
    if (!hatMehrereStufen) return null;
    if (!selSet.has(pid)) return null;
    // Fehlt der Eintrag fuer diese Pid (kann nach externen Renderpfaden passieren),
    // gelten implizit alle Stufen - identisch zum Backend-Default "*".
    const aktive = Array.isArray(pidStufen[pid]) ? new Set(pidStufen[pid]) : new Set(allStageKeys);
    return (
      <div className="pd-stages" role="group" aria-label={t("s5.pdStagesAria", { pid })}>
        <span className="pd-stages-lbl">{t("s5.pdStagesLbl")}</span>
        {stufenListe.map((st) => {
          const an = aktive.has(st.key);
          return (
            <button
              key={st.key}
              type="button"
              className={"pd-stage-toggle" + (an ? " is-on" : "")}
              aria-pressed={an}
              // Klick auf die Zeile (label) wuerde sonst die Pid-Checkbox toggeln - hier
              // gehoert der Klick zur Stufenzeile, nicht zur Pid-Auswahl.
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleStage(pid, st.key); }}
              title={t(an ? "s5.pdStageOn" : "s5.pdStageOff", { code: st.code || stageLabel(st.key) })}
            >
              <span className="pd-stage-mark" aria-hidden>{an ? "✓" : ""}</span>
              <span className="pd-stage-code">{st.code || stageLabel(st.key)}</span>
            </button>
          );
        })}
      </div>
    );
  };

  // "Eigene hinzufuegen": alles aus pdSource MINUS automatisch erkannt (via reference
  // ODER via header - beides schon in Block 1). Systemeigene Kennungen bleiben als
  // ausgegraute Zeile.
  const custom = (pdSource.entries || []).filter((e) => !detectedPidSet.has(e.Pid));
  const q = pdSearch.trim().toLowerCase();
  const customFiltered = q ? custom.filter((e) => e.Pid.toLowerCase().includes(q)) : custom;

  // Anzahlen fuer die Kopfzeile
  const selectableCustom = custom.filter((e) => !e.systemGrund);
  const selCustomCount = selectableCustom.filter((e) => selSet.has(e.Pid)).length;
  const selAutoCount = [...detectedPidSet].filter((p) => selSet.has(p)).length;
  const totalSelected = selectedPids.length;
  const totalDetected = detectedPidSet.size;

  const setAllCustom = (on) => {
    const cur = new Set(selectedPids);
    for (const e of selectableCustom) {
      if (on) cur.add(e.Pid); else cur.delete(e.Pid);
    }
    onSetAll([...cur]);
  };
  const setAllAuto = (on) => {
    const cur = new Set(selectedPids);
    for (const p of detectedPidSet) {
      if (on) cur.add(p); else cur.delete(p);
    }
    onSetAll([...cur]);
  };

  return (
    <div className="pd-picker">
      {/* Zusammenfassungszeile */}
      <div className="pd-picker-sum">
        {totalSelected > 0
          ? t("s5.pdSelSummary", { n: totalSelected })
          : t("s5.pdSelSummaryNone")}
      </div>

      {/* Block 1: Automatisch erkannt */}
      <div className="pd-block">
        <div className="pd-block-h">
          <div>
            <div className="pd-block-title">{t("s5.pdAutoTitle")}</div>
            <div className="pd-block-sub">{t("s5.pdAutoSub")}</div>
          </div>
          <div className="pd-block-actions">
            <span className="tiny muted">{t("s5.pdSelCount", { n: selAutoCount, m: totalDetected })}</span>
            {totalDetected > 0 && (
              <>
                <button type="button" className="pd-mini-btn" onClick={() => setAllAuto(true)}>{t("s5.pdAll")}</button>
                <button type="button" className="pd-mini-btn pd-mini-btn--ghost" onClick={() => setAllAuto(false)}>{t("s5.pdNone")}</button>
              </>
            )}
          </div>
        </div>
        {pdAuto.loading && <Skeleton variant="rows" count={3} columns={3} label={t("s5.pdAutoLoading")} />}
        {!pdAuto.loading && totalDetected === 0 && <div className="pd-empty">{t("s5.pdAutoNone")}</div>}
        {!pdAuto.loading && totalDetected > 0 && (
          <div className="pd-list">
            {anzeige.map((eintrag) => {
              // Rolle bestimmt Text + optionalen Header-Kontext (Sender/Interface).
              const rolleTxt = eintrag.rolle === "related"
                ? t("s5.pdRoleRelated")
                : eintrag.rolle === "header"
                  ? t("s5.pdRoleHeader")
                  : t("s5.pdRolePrimary");
              const headerCtx = eintrag.rolle === "header" && eintrag.sender
                ? (eintrag.iface
                    ? t("s5.pdHeaderCtx", { s: eintrag.sender, i: eintrag.iface })
                    : t("s5.pdHeaderCtxShort", { s: eintrag.sender }))
                : null;
              return (
                <div key={eintrag.pid} className="pd-item-wrap">
                  <label className="pd-item">
                    <input
                      type="checkbox"
                      className="cb"
                      checked={selSet.has(eintrag.pid)}
                      onChange={() => onToggle(eintrag.pid)}
                    />
                    <span className="pd-pid">{eintrag.pid}</span>
                    {headerCtx && <span className="pd-meta">{headerCtx}</span>}
                    <span className="pd-role">{rolleTxt}</span>
                  </label>
                  {renderStufen(eintrag.pid)}
                </div>
              );
            })}
          </div>
        )}
        {pdAuto.error && !pdAuto.loading && (
          <div className="pd-empty" style={{ color: "var(--warn)" }}>{pdAuto.error}</div>
        )}
        {hatMehrereStufen && totalDetected > 0 && !pdAuto.loading && (
          <div className="pd-stages-hint">{t("s5.pdStagesHint")}</div>
        )}
      </div>

      {/* Block 2: Eigene hinzufuegen */}
      <div className="pd-block">
        <div className="pd-block-h">
          <div>
            <div className="pd-block-title">{t("s5.pdCustomTitle")}</div>
            <div className="pd-block-sub">{t("s5.pdCustomSub")}</div>
          </div>
          <div className="pd-block-actions">
            <span className="tiny muted">{t("s5.pdSelCount", { n: selCustomCount, m: selectableCustom.length })}</span>
            {selectableCustom.length > 0 && (
              <>
                <button type="button" className="pd-mini-btn" onClick={() => setAllCustom(true)}>{t("s5.pdAll")}</button>
                <button type="button" className="pd-mini-btn pd-mini-btn--ghost" onClick={() => setAllCustom(false)}>{t("s5.pdNone")}</button>
              </>
            )}
          </div>
        </div>
        {pdSource.loading && <Skeleton variant="rows" count={5} columns={3} label={t("s5.pdCustomLoading")} />}
        {!pdSource.loading && (pdSource.entries || []).length === 0 && (
          <div className="pd-empty">{t("s5.pdCustomEmpty")}</div>
        )}
        {!pdSource.loading && custom.length > 0 && (
          <>
            <input
              type="text"
              className="pd-search"
              placeholder={t("s5.pdCustomSearch")}
              value={pdSearch}
              onChange={(e) => setPdSearch(e.target.value)}
            />
            <div className="pd-list pd-list--scroll">
              {customFiltered.map((entry) => {
                const isSystem = !!entry.systemGrund;
                const meta = t("s5.pdCountsMeta", {
                  s: (entry.stringParams || []).length,
                  b: (entry.binaryParams || []).length,
                  a: (entry.altPartners || []).length,
                });
                return (
                  <div key={entry.Pid} className="pd-item-wrap">
                    <label className={"pd-item" + (isSystem ? " pd-item--disabled" : "")}>
                      <input
                        type="checkbox"
                        className="cb"
                        disabled={isSystem}
                        checked={!isSystem && selSet.has(entry.Pid)}
                        onChange={() => !isSystem && onToggle(entry.Pid)}
                      />
                      <span className="pd-pid">{entry.Pid}</span>
                      <span className="pd-meta">{meta}</span>
                      {isSystem && <span className="pd-sys">{t("s5.pdSystem")}</span>}
                    </label>
                    {!isSystem && renderStufen(entry.Pid)}
                  </div>
                );
              })}
            </div>
          </>
        )}
        {pdSource.error && !pdSource.loading && (
          <div className="pd-empty" style={{ color: "var(--warn)" }}>{pdSource.error}</div>
        )}
      </div>
    </div>
  );
}
